<template>
    <div v-if="highlighted_categories && highlighted_categories.length" class="relative">
        <SectionIntro
            :title="$t('products.highlighted_categories.title')"
            :subtitle="$t('products.highlighted_categories.subtitle')"
            class="mt-4"
        />
        <div
            ref="scrollContainer"
            class="flex w-[calc(100%+2.5rem)] -ml-5 lg:ml-0 lg:w-full flex-nowrap gap-x-4 lg:gap-x-6 items-center overflow-x-scroll nicescroll lg:overflow-visible px-4 lg:px-0 pb-4 lg:pb-0"
            :class="
                isOverlay
                    ? 'text-white justify-start mb-6 pb-4 border-b border-white border-opacity-20'
                    : 'justify-start lg:justify-center mb-6'
            "
        >
            <h3 v-if="isOverlay" class="font-Inter-Bold lg:mr-4 uppercase text-lg lg:text-xl hidden md:flex">
                {{ $t('footer.featured_categories') }}
            </h3>
            <button
                v-for="category in cats.slice(0, 7)"
                :key="`category-${category.translations[0].id}`"
                :ref="(el) => setButtonRef(String(category.translations[0].id), el)"
                class="whitespace-nowrap font-Inter-Bold bg-opacity-0 hover:bg-opacity-5 py-1 px-5 rounded-full transition border hover:border-opacity-100 border-transparent hover:text-blue-100"
                :class="{
                    'hover:text-white':
                        (isOverlay && isActiveTab(category.translations[0].id)) ||
                        selectedHighlightedCategory === parseInt(category.translations[0].id),
                    'text-white hover:text-white border-opacity-100 bg-opacity-100 hover:bg-opacity-100 pointer-events-none':
                        isActiveTab(category.translations[0].id) ||
                        selectedHighlightedCategory === parseInt(category.translations[0].id),
                    'bg-blue-100 hover:border-blue-100': !isOverlay,
                    'bg-white': isOverlay,
                }"
                @click="setHighlightedCategories(category.translations[0].id)"
            >
                {{ category.translations[0].name }}
            </button>
        </div>
        <div v-if="products && products.length">
            <div class="grid grid-cols-10 gap-3 lg:gap-4 mb-8 lg:mb-12">
                <div
                    v-for="product in products.slice(0, $device.isDesktop ? 5 : 6)"
                    :key="`product-${product.id}`"
                    class="col-span-5 lg:col-span-2 h-full"
                    :class="product?.has_banner ? 'lg:col-span-12' : ' col-span-5 lg:col-span-3'"
                >
                    <div class="h-full" :class="product?.has_banner && 'lg:grid lg:grid-cols-12 lg:gap-x-8'">
                        <div class="h-full" :class="product?.has_banner && 'lg:col-span-3'">
                            <ProductCard
                                :title="product.name"
                                :rating="5"
                                :has-btn-text="false"
                                :product="product"
                                :slug="
                                    localePath({
                                        name: 'products-category-product',
                                        params: {
                                            category: product?.category_slug,
                                            product: product?.slug,
                                        },
                                    })
                                "
                                class="h-full"
                                :brutto-price="product.price"
                                :is-favorite="product.isFavorite"
                                :price="product.price"
                                :discounted-price="product.discounted_price"
                                :md="$device.isMobile"
                                :image="product.category_name === 'Whispers of Wood' ? product.image : product.preview"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="mb-8 lg:mb-24 flex justify-center">
                <more-button
                    :to="
                        localePath({
                            name: 'products-category',
                            params: { category: activeCategory.translations[0].slug },
                        })
                    "
                    :label="$t('products.highlighted_categories.more_in_category', { name: activeCategory.translations[0].name })"
                />
            </div>
        </div>
        <div v-else class="text-center mb-12 lg:mb-40 flex flex-col items-center justify-center h-full mt-16">
            <i class="sad-emoji mb-3" data-not-lazy></i>
            <h4 class="font-Inter-Bold text-xl lg:text-[1.5rem] mb-4 leading-[2rem]" v-html="$t('products.no_results_title')"></h4>
            <p class="lg:w-8/12 text-sm lg:text-base">
                {{ $t('products.no_results_text') }}
            </p>
        </div>
    </div>
</template>

<script>
import ProductCard from '~/components/Cards/ProductCard/ProductCard.vue';
import MoreButton from '~/components/UI/Buttons/MoreButton.vue';
export default {
    name: 'HighlightedCategories',
    components: {
        MoreButton,
        ProductCard,
    },
    props: {
        isOverlay: {
            type: Boolean,
            required: false,
            default: false,
        },
        cats: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            products: [],
            selectedHighlightedCategory: null,
            highlighted_categories: [],
            categories: [],
            buttonRefs: {},
        };
    },
    computed: {
        activeCategory() {
            return this.cats.find(
                (category) => parseInt(category.translations[0].id) === parseInt(this.selectedHighlightedCategory)
            );
        },
    },
    watch: {
        cats: {
            immediate: true,
            handler(newCats) {
                if (newCats && newCats.length) {
                    this.highlighted_categories = newCats;
                    // Initialize with first category if none selected
                    if (!this.selectedHighlightedCategory && newCats[0]?.translations?.[0]?.id) {
                        const firstCategoryId = newCats[0].translations[0].id;
                        this.setHighlightedCategories(firstCategoryId);
                    }
                }
            },
        },
        selectedHighlightedCategory() {
            this.scrollActiveToCenter();
        },
    },
    mounted() {
        // Initialize on mount if cats are already available
        if (this.cats && this.cats.length && !this.selectedHighlightedCategory) {
            this.highlighted_categories = this.cats;
            const firstCategoryId = this.cats[0]?.translations?.[0]?.id;
            if (firstCategoryId) {
                this.setHighlightedCategories(firstCategoryId);
            }
        }
    },
    methods: {
        setButtonRef(id, el) {
            if (el) {
                this.buttonRefs[id] = el;
            } else {
                delete this.buttonRefs[id];
            }
        },
        setHighlightedCategories(currentCategory) {
            this.selectedHighlightedCategory = parseInt(currentCategory);
            const category = this.highlighted_categories.find(
                (cat) => parseInt(cat.translations[0].id) === parseInt(currentCategory)
            );
            this.products = category?.products || [];
        },
        isActiveTab(tabId) {
            return parseInt(this.selectedHighlightedCategory) === parseInt(tabId);
        },
        scrollActiveToCenter() {
            if (!this.$device?.isMobile) return;
            this.$nextTick(() => {
                const id = String(this.selectedHighlightedCategory);
                const el = this.buttonRefs[id];
                const container = this.$refs.scrollContainer;

                if (el && container) {
                    // Calculate the position to center the button
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = el.getBoundingClientRect();
                    const scrollLeft = container.scrollLeft;
                    const elementLeft = elementRect.left - containerRect.left + scrollLeft;
                    const elementWidth = elementRect.width;
                    const containerWidth = containerRect.width;

                    // Calculate the scroll position to center the element
                    const targetScrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

                    // Use scrollTo for better Safari compatibility
                    container.scrollTo({
                        left: targetScrollLeft,
                        behavior: 'smooth',
                    });
                }
            });
        },
    },
};
</script>
